import React from 'react';
import { Alert, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/productionFormat';

interface ProductionCostSummaryProps {
  standardUnitCost?: number | null;
  standardTotalCost?: number | null;
  actualUnitCost?: number | null;
  actualTotalCost?: number | null;
  mainAllocatedCost?: number | null;
  standardMaterialCost?: number | null;
  standardConversionCost?: number | null;
  standardOverheadCost?: number | null;
  actualMaterialCost?: number | null;
  actualMachineCost?: number | null;
  actualLaborCost?: number | null;
  actualOverheadCost?: number | null;
  hasOutputs?: boolean;
  costWarning?: string | null;
}

function moneyOrDash(value?: number | null, digits = 2): string {
  return value == null ? '—' : formatMoney(value, digits);
}

function varianceOf(actual?: number | null, standard?: number | null): number | null {
  if (actual == null || standard == null) return null;
  return actual - standard;
}

const ProductionCostSummary: React.FC<ProductionCostSummaryProps> = ({
  standardUnitCost,
  standardTotalCost,
  actualUnitCost,
  actualTotalCost,
  mainAllocatedCost,
  standardMaterialCost,
  standardConversionCost,
  standardOverheadCost,
  actualMaterialCost,
  actualMachineCost,
  actualLaborCost,
  actualOverheadCost,
  hasOutputs,
  costWarning,
}) => {
  const { t } = useTranslation('production');
  const actualConversion = actualMachineCost == null && actualLaborCost == null
    ? null
    : (actualMachineCost ?? 0) + (actualLaborCost ?? 0);
  // standardTotalCost ana ürünün payıdır; çıktılı fişte brüt toplam bileşenlerden kurulur.
  const standardGross = standardMaterialCost == null && standardConversionCost == null && standardOverheadCost == null
    ? null
    : (standardMaterialCost ?? 0) + (standardConversionCost ?? 0) + (standardOverheadCost ?? 0);
  const rows: Array<{ label: string; standard?: number | null; actual?: number | null; digits?: number }> = [
    { label: t('cost.material'), standard: standardMaterialCost, actual: actualMaterialCost },
    { label: t('cost.machine'), standard: null, actual: actualMachineCost },
    { label: t('cost.labor'), standard: null, actual: actualLaborCost },
    { label: t('cost.conversion'), standard: standardConversionCost, actual: actualConversion },
    { label: t('cost.overhead'), standard: standardOverheadCost, actual: actualOverheadCost },
    { label: t('cost.total'), standard: hasOutputs ? standardGross : standardTotalCost, actual: actualTotalCost },
  ];
  if (hasOutputs) {
    rows.push({ label: t('cost.mainAllocated'), standard: standardTotalCost, actual: mainAllocatedCost ?? actualTotalCost });
  }
  rows.push({
    label: t('cost.unit'),
    standard: standardUnitCost,
    actual: actualUnitCost,
    digits: 4,
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('detail.cost')}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right">{t('cost.standard')}</TableCell>
            <TableCell align="right">{t('cost.actual')}</TableCell>
            <TableCell align="right">{t('cost.variance')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const variance = varianceOf(row.actual, row.standard);
            const digits = row.digits ?? 2;
            return (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{moneyOrDash(row.standard, digits)}</TableCell>
                <TableCell align="right">{moneyOrDash(row.actual, digits)}</TableCell>
                <TableCell align="right">{moneyOrDash(variance, digits)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {costWarning ? <Alert severity="warning" sx={{ mt: 1 }}>{costWarning}</Alert> : null}
    </Paper>
  );
};

export default ProductionCostSummary;
